/**
 * Ops-friendly calendar date for inventory / list tables.
 * Local calendar day as zero-padded `YYYY-MM-DD` (not locale `M/D/YYYY`).
 */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Ops-friendly timestamp for scan runs / audit rows: `YYYY-MM-DD HH:mm` local.
 * Same calendar-day base as formatDate; avoids locale `M/D/YYYY, h:mm:ss AM`.
 */
export function formatDateTime(iso?: string | null): string {
  const day = formatDate(iso);
  if (day === '—') return day;
  const d = new Date(iso as string);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${hh}:${mm}`;
}

/**
 * Elapsed span between two timestamps, for "resolved in" style columns:
 * `3d 4h` · `5h 12m` · `47m` · `< 1m`. Deliberately coarse to two units —
 * a turnaround time is read at a glance and seconds are noise at this scale.
 * Either end missing (e.g. a still-pending request) → `—`.
 */
export function formatDuration(fromIso?: string | null, toIso?: string | null): string {
  if (!fromIso || !toIso) return '—';
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return '—';
  const mins = Math.floor((to - from) / 60_000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
